import { ChangeEvent, useCallback, useContext, useRef, useState, MouseEvent, useEffect, useMemo } from "react";

import { ITaskAddEditFormProps } from "../../component/Task/TaskAddEditForm/TaskAddEditForm";
import { IDashBoard } from "../../common/api_interfaces/board/IDashBoard";
import { Context } from "../..";
import { AppContext } from "../../App";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Validator } from "../../helpers/TaskAddEditFunc/taskAddEditFunc";
import { createArgumentIfDefined, IArgument, urlClear, urlFiller } from "../../helpers/paramsAutoFiller";
import { ITaskSaveOptions } from "../../models/TaskModels/ITaskSaveOptions";
import TaskType from "../../component/Shared/TaskType";
import TaskPriority from "../../component/Shared/TaskPriority";
import { ITeamFilter } from "../../component/Project/Team/Team";
import axios, { AxiosError } from "axios";
import CustomAlert from "../../component/Shared/CustomAlert/CustomAlert";
import { ITaskEdit } from "../../common/api_interfaces/task/ITaskEdit";
import { ITag } from "../../common/api_interfaces/tag/ITag";
import { ITeamMemberDisplay } from "../../common/api_interfaces/project/ITeamDisplay";
import { IGetTeamDisplay } from "../../common/api_interfaces/project/IGetTeamDisplay";
import { ITaskFormDisplay } from "../../common/api_interfaces/task/ITaskFormDisplay";
import { IUserMinimal } from "../../common/api_interfaces/user/IUserMinimal";
import { ITaskCreate } from "../../common/api_interfaces/task/ITaskCreate";
import { ITypeAndPriority } from "../../common/api_interfaces/task/ITypeAndPriority";
import { getChangedFields } from "../../helpers/getChangedFields";

interface StyleItem {
    color: string;
    icon: string;
}

interface TaskOption {
    id: number | string;
    name: string;
    style: string;
}

interface CalendarChangeEvent {
    value: Date | Date[] | null;
    originalEvent: React.SyntheticEvent;
}

const OLD_DATE = "16.05.2025";

export const useFuncTaskForm = (props: ITaskAddEditFormProps) => {
    const {
        mode,
        taskId,
        parentTaskId,
        columnId,
        projectId,
        permission,
        optionsQueryParams,
        responsibleRequired,
        closeFunc,
        defaultBoardId,
        onTaskAction,
    } = props;

    // #region Контекст

    const { store } = useContext(Context);
    const { showToast, handleShowLoader } = useContext(AppContext);

    // #endregion

    const navigate = useNavigate();

    //Параметры для логики
    const [isLoading, setIsLoading] = useState(true);
    const [taskName, setTaskName] = useState<string>("");
    const [taskDesc, setTaskDesc] = useState<string>("");
    const [taskOldMarkDesc, setTaskOldMarkDesc] = useState<string>("");
    const [taskData, setTaskData] = useState<ITaskFormDisplay | undefined>();
    const [typesPriority, setTypesPriority] = useState<ITypeAndPriority | undefined>();
    const [showResponsibleSelector, setShowResponsibleSelector] = useState<boolean>(false);
    const [scrollEnded, setScrollEnded] = useState<boolean>(false);
    const [persons, setPersons] = useState<ITeamMemberDisplay[]>([]);
    const [boards, setBoards] = useState<IDashBoard[]>();
    const [boardId, setBoardId] = useState<number>();
    const [boardName, setBoardName] = useState<string>();
    const [type, setType] = useState<number>();
    const [priority, setPriority] = useState<number>();
    const [tags, setTags] = useState<ITag[]>([]);
    const [percentageCompletion, setPercentageCompletion] = useState<number>(0);
    const [tab, setTab] = useState<number>(0);
    const [customHours, setCustomHours] = useState<number | undefined>(undefined);
    const [customMinutes, setCustomMinutes] = useState<number | undefined>(undefined);
    const [showGroupSelector, setShowGroupSelector] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();
    const [maxDescription, setMaxDescription] = useState<boolean>(false);
    const [curDescriptionLength, setCurDescriptionLength] = useState<number>(0);

    const [group, setGroup] = useState<IUserMinimal[]>([]);
    const [responsible, setResponsible] = useState<IUserMinimal | null>();
    const [preEditResp, setPreEditResp] = useState<IUserMinimal | null>();

    const [preEditCompiledRequest, setPreEditCompileRequest] = useState<ITaskEdit | ITaskCreate>();

    //Валидация
    const [nameError, setNameError] = useState<string>();
    const [descError, setDescError] = useState<string>();
    const [datesError, setDatesError] = useState<string>();
    const [timeSelectVariant, setTimeSelectVariant] = useState<number>();
    const [priorityError, setPriorityError] = useState<string>();
    const [boardError, setBoardError] = useState<string>();
    const [responsibleError, setResponsibleError] = useState<string>();
    const [typeError, setTypeError] = useState<string>();
    const [openConfirmWindow, setOpenConfirmWindow] = useState(false);
    const [openBoardsSelect, setBoardsSelect] = useState(false);
    const [openOldMark, setOpenOldMark] = useState(false);
    const [showWarning, setShowWarning] = useState<boolean>(false);

    // Параметры строки
    const [searchParams, setSearchParams] = useSearchParams();

    const forceLinkTerm = useMemo(() => taskData?.externalParams?.forceLinkTerm === true, [taskData]);
    const forceLinkName = useMemo(() => taskData?.externalParams?.forceLinkName === true, [taskData]);
    const forceLinkDescription = useMemo(() => taskData?.externalParams?.forceLinkDescription === true, [taskData]);
    const forceLinkExecutor = useMemo(() => taskData?.externalParams?.forceLinkExecutor === true, [taskData]);

    // Марк даун
    const [editValue, setEditValue] = useState("");
    const [value, setValue] = useState("");
    const [fileCreate, setFileCreate] = useState<number[]>([]);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    // #endregion

    // #region Функции

    // Вычилсение времени
    const calculateRequiredTime = useCallback((): number => {
        if (customHours || customMinutes) {
            const hours = customHours ? Number(customHours) * 60 : 0;
            const minutes = customMinutes ? Number(customMinutes) : 0;
            return hours + minutes;
        }
        return timeSelectVariant ? timeSelectVariant * 60 : 0;
    }, [customHours, customMinutes, timeSelectVariant]);

    // Фильтрация тегов
    const filterTags = useCallback(() => {
        const oldTags = tags?.filter((tag) => tag.id > 0) || [];
        const newTags = tags?.filter((tag) => tag.id < 0) || [];
        return { oldTags, newTags };
    }, [tags]);

    // Нормализация описания
    const normalizeDescription = useCallback((description: string): string => {
        return description === "<p><br></p>" ? "" : description;
    }, []);

    // Нормализация даты
    const normalizeDate = useCallback((date: Date | null | undefined): Date | undefined => {
        return date ? new Date(date.toUTCString()) : undefined;
    }, []);

    // Сборка тела запроса
    const compileRequest = useCallback((): ITaskCreate | ITaskEdit => {
        // if (!type || !priority) {
        //     throw new Error("Обязательные поля не заполнены");
        // }

        let reqTimeFormat = calculateRequiredTime();
        const { oldTags, newTags } = filterTags();
        const descriptionFormat = normalizeDescription(value);
        const groupIdsFormat = group?.map((x) => x.id).filter((id) => id !== responsible?.id) || [];
        const responsibleFormat = responsible?.id ?? null;

        // Базовые поля, общие для обоих интерфейсов
        const baseFields = {
            name: taskName.trim(),
            description: descriptionFormat,
            type: type,
            responsible: responsibleFormat,
            group: groupIdsFormat,
            startDate: normalizeDate(startDate)!,
            endDate: normalizeDate(endDate)!,
            requiredTime: reqTimeFormat,
            files: fileCreate || [],
            priorityId: priority,
            percentageCompletion: percentageCompletion,
            tags: {
                old: oldTags.length > 0 ? oldTags.map((tag) => tag.id) : [],
                new: newTags.length > 0 ? newTags.map(({ name, color }) => ({ name, color })) : [],
            },
        };

        // Если есть taskData - возвращаем ITaskEdit
        if (taskData) {
            const req: ITaskEdit = {
                id: taskData.id,
                ...baseFields,
                tags: {
                    ...baseFields.tags,
                },
            }
            return req;
        }

        if (boardId === undefined || boardId === null) {
            throw Error('Не заполнен boardId');
        }

        // Если нет taskData - возвращаем ITaskCreate
        const req: ITaskCreate = {
            projectId: projectId ?? store.currentProjectId,
            boardId: boardId,
            columnId: columnId,
            ...baseFields,
            parentTaskId: parentTaskId,
            type: baseFields.type ?? 1,
            priorityId: baseFields.priorityId ?? 1
        };
        return req;
    }, [
        customHours,
        customMinutes,
        timeSelectVariant,
        tags,
        value,
        projectId,
        store.currentProjectId,
        boardId,
        columnId,
        taskName,
        type,
        priority,
        group,
        responsible,
        startDate,
        endDate,
        percentageCompletion,
        fileCreate,
        parentTaskId,
        taskData,
        calculateRequiredTime,
    ]);

    // Сборка значений для сохранения в сторе
    const compileSaveParam = useCallback((): ITaskSaveOptions => {
        const reqTime = calculateRequiredTime();
        const { oldTags, newTags } = filterTags();
        const descriptionFormat = normalizeDescription(value);

        return {
            name: taskName.trim(),
            description: descriptionFormat,
            boardId: boardId,
            type: type!,
            priorityId: priority!,
            requiredTime: reqTime,
            group: group,
            responsible: responsible,
            endDate: normalizeDate(endDate),
            startDate: normalizeDate(startDate),
            percentageCompletion: Number(percentageCompletion),
            tags: {
                old: oldTags.length > 0 ? oldTags.map((tag) => tag.id) : [],
                new: newTags.length > 0 ? newTags.map(({ name, color }) => ({ name, color })) : [],
            },
        };
    }, [
        customHours,
        customMinutes,
        timeSelectVariant,
        tags,
        value,
        taskName,
        boardId,
        type,
        priority,
        group,
        responsible,
        endDate,
        startDate,
        percentageCompletion,
    ]);

    // Создание/редактирование задачи
    const handleTaskAction = useCallback(async () => {
        handleShowLoader(true);

        try {
            const validationErrors = [
                Validator.validateName(taskName, setNameError),
                Validator.validateDesc(maxDescription, setDescError),
                Validator.validateDates(startDate, endDate, setDatesError),
                Validator.validateType(type, setTypeError),
                Validator.validateResponsible(responsible, responsibleRequired, setResponsibleError),
                Validator.validatePriority(priority, setPriorityError),
                Validator.validateBoard(columnId, boardId, taskData, setBoardError),
            ];

            if (validationErrors.some(Boolean)) return;

            const request = compileRequest();
            const saveParams = compileSaveParam();
            store.setAddEditTaskData(optionsQueryParams, saveParams);

            let taskId: number | undefined;

            if (!taskData) {
                const response = await store.taskService.createTask(request as ITaskCreate);
                taskId = response.data.id;
                showToast("Задача создана", <Link to={`/task/${taskId}`}>Перейти к задаче</Link>);
            } else {
                let editRequest = request as ITaskEdit;
                if (preEditCompiledRequest) {
                    editRequest = getChangedFields<ITaskEdit>(
                        preEditCompiledRequest as ITaskEdit,
                        editRequest
                    ) as ITaskEdit;
                }
                editRequest.id = taskData.id;

                await store.taskService.editTask(editRequest as ITaskEdit);
                taskId = taskData.id;
                showToast("Задача обновлена");
            }

            store.clearAddEditTaskData();
            urlClear(window.location.href, ["page", "entity", "action", "entityId", "checklistId"], navigate);
            onTaskAction?.();
            closeFunc();
            store.setDrag(true);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data.message);
            } else {
                showToast(`Произошла ошибка! ${error}`);
            }
        } finally {
            handleShowLoader(false);
        }
    }, [
        taskName,
        startDate,
        endDate,
        type,
        responsible,
        priority,
        columnId,
        boardId,
        taskData,
        optionsQueryParams,
        responsibleRequired,
        compileRequest,
        compileSaveParam,
        onTaskAction,
        closeFunc,
        store,
        setSearchParams,
    ]);

    // Общая функция рендера компонента
    const renderTaskOptions = (
        items: TaskOption[] | undefined,
        selectedId: number | string | undefined | null,
        onSelect: (id: number) => void,
        Component: React.ComponentType<{ name: string; color: string; icon: string }>
    ): JSX.Element[] | null => {
        if (!items?.length) return null;

        return items.map((item) => {
            const styleItem: StyleItem = JSON.parse(item.style);

            return (
                <button
                    onClick={() => onSelect(Number(item.id))}
                    style={{ cursor: "pointer" }}
                    key={item.id}
                    className={selectedId !== item.id ? "opacity-40" : ""}
                >
                    <Component name={item.name} color={styleItem.color} icon={styleItem.icon} />
                </button>
            );
        });
    };

    // Тип задачи
    const taskTypeDisplay = (): JSX.Element[] | null => {
        return renderTaskOptions(typesPriority?.type, type, handleTypeSelection, TaskType);
    };

    // Приоритет задачи
    const taskPriorityDisplay = (): JSX.Element[] | null => {
        return renderTaskOptions(typesPriority?.priority, priority, handlePrioritySelection, TaskPriority);
    };

    // Установка типа
    const handleTypeSelection = useCallback((typeId: number) => {
        setType(typeId);
    }, []);

    // Установка приоритета
    const handlePrioritySelection = useCallback((priorityId: number) => {
        setPriority(priorityId);
    }, []);

    // Смена кастомного времени
    const onChangeCustomTime = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setTimeSelectVariant(undefined);

            const { id, value } = event.target;
            const numericValue = value.replace(/\D/g, "");

            switch (id) {
                case "custom-hours-input":
                    setCustomHours(Number(numericValue.slice(0, 4)));
                    break;
                case "custom-minutes-input":
                    setCustomMinutes(Math.min(59, Number(numericValue.slice(0, 2))));
                    break;
            }
        },
        [setTimeSelectVariant, setCustomHours, setCustomMinutes]
    );

    // Установка выбранного времени
    const selectTimeVariant = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            const timeText = Number(event.currentTarget.textContent);

            setTimeSelectVariant(timeText);
            setCustomHours(timeText);
            setCustomMinutes(0);
        },
        [setTimeSelectVariant, setCustomHours, setCustomMinutes]
    );

    // Загрузка команды проекта
    const loadPersons = useCallback(
        async (filters: ITeamFilter) => {
            const newPageReq: IGetTeamDisplay = { filters };

            try {
                const res = await store.projectService.getProjectTeamDisplayData(newPageReq);
                setPersons(res.data.team || []);
            } catch (error) {
                if (error instanceof AxiosError) {
                    showToast(error.response?.data?.message || "Ошибка загрузки команды");
                }
            }
        },
        [store.projectService, setPersons, showToast]
    );

    // Загрузка команды проекта (вызов loadPersons)
    const loadTeamMembers = useCallback(async () => {
        await loadPersons({
            projectId: projectId ?? store.currentProjectId,
            name: undefined,
            role: undefined,
        });
    }, [loadPersons, projectId, store.currentProjectId]);

    // Очистка ответственного
    const clearResponsible = useCallback(() => {
        setGroup((prevValue) =>
            preEditResp && !prevValue.find((xx) => xx.id === preEditResp?.id) ? [...prevValue, preEditResp] : prevValue
        );
        setResponsible(undefined);
    }, [setResponsible, setGroup, preEditResp]);

    // Установка группы соисполнителей
    const handleGroupSelection = useCallback(
        (personId: number) => {
            const person = persons?.find((p) => p.id === personId);

            if (person?.id === responsible?.id) {
                clearResponsible();
            }

            if (person) {
                setGroup((prevGroup) => [
                    ...prevGroup,
                    {
                        ...person,
                        middlename: person.middlename,
                        photoId: person.photoId,
                    },
                ]);
            }
        },
        [persons, responsible, setGroup, clearResponsible]
    );

    // Закрыть селектор выбора пользовтелей в группу
    const closeGroupSelector = useCallback(() => {
        setShowGroupSelector(false);
    }, [setShowGroupSelector]);

    // Открыть селектор выбора пользователей в группу
    const openGroupSelector = useCallback(async () => {
        setShowGroupSelector(true);

        await loadPersons({
            projectId: projectId ?? store.currentProjectId,
            name: undefined,
            role: undefined,
        });
    }, [setShowGroupSelector, loadPersons, projectId, store.currentProjectId]);

    // Заркыть селектор выбора ответственного
    const closeResponsibleSelector = useCallback(() => {
        setShowResponsibleSelector(false);
    }, [setShowResponsibleSelector]);

    // Управление селектором ответственного
    const openResponsibleSelector = useCallback(() => {
        setShowResponsibleSelector(true);
    }, [setShowResponsibleSelector]);

    // Удаление из группы
    const handleGroupRemove = useCallback(
        (personId: number) => {
            setGroup((prevGroup) => prevGroup?.filter((person) => person.id !== personId) || []);
        },
        [setGroup]
    );

    // Установка ответственного
    const handleResponsibleSelection = useCallback(
        (personId: number) => {
            const person = persons?.find((p) => p.id === personId);

            if (group.find((xx) => xx.id === personId)) {
                handleGroupRemove(personId);
            }

            if (!person) {
                showToast("Не удалось назначить пользователя");
                return;
            }

            const personMinimal: IUserMinimal = {
                id: person.id,
                name: person.name,
                surname: person.surname,
                middlename: person.middlename ?? undefined,
                photoId: person.photoId ?? undefined,
            };

            if (person) {
                setResponsible(personMinimal);
                closeResponsibleSelector();
            }
        },
        [persons, group, setResponsible, closeResponsibleSelector, handleGroupRemove]
    );

    // Добавление пользователя в группу
    const appendPersons = useCallback(
        async (skip: number, take: number, filters: ITeamFilter) => {
            const newPageReq: IGetTeamDisplay = { filters };

            try {
                const res = await store.projectService.getProjectTeamDisplayData(newPageReq);

                if (res.data?.team?.length > 0) {
                    setPersons((prevPersons) => [...prevPersons, ...res.data.team]);
                } else {
                    setScrollEnded(true);
                }
            } catch (error) {
                if (error instanceof AxiosError) {
                    showToast(error.response?.data?.message || "Ошибка загрузки данных");
                } else {
                    showToast("Неизвестная ошибка");
                }
            }
        },
        [store.projectService, setPersons, setScrollEnded, showToast]
    );

    // Добавление тегов
    const onAddTags = useCallback(
        async (_tags: ITag[]) => {
            setTags((prevTags) => {
                const newTags = prevTags?.filter((tag) => tag.id < 0) || [];
                const mergedTags = [...newTags, ..._tags];
                return mergedTags.filter((tag, index, self) => self.findIndex((t) => t.id === tag.id) === index);
            });
        },
        [setTags]
    );

    // Вылидатор даты выполнения задачи
    const validateDates = (startDate: Date | undefined, endDate: Date | undefined) => {
        // Оба значения не указаны - это валидный случай (пустые значения)
        if (!startDate && !endDate) {
            setDatesError(undefined);
            return;
        }

        // Дата окончания указана, а дата начала нет - ошибка
        if (!startDate && endDate) {
            setDatesError('Дата окончания не может существовать без даты начала');
            return;
        }

        // Только дата начала указана - это валидный случай
        if (startDate && !endDate) {
            setDatesError(undefined);
            return;
        }

        // Обе даты указаны - проверяем, что дата начала не позже даты окончания
        if (startDate && endDate) {
            // Сбрасываем время для корректного сравнения только дат
            const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

            if (start > end) {
                setDatesError('Дата начала не может быть позже даты окончания');
                return;
            }
        }

        setDatesError(undefined);
        return;
    };

    // Обработчик изменения даты начала
    const onChangeStartDate = useCallback(
        (e: CalendarChangeEvent) => {
            if (e.value instanceof Date) {
                setStartDate(e.value);
                validateDates(e.value, endDate);
            } else if (Array.isArray(e.value) && e.value[0] instanceof Date) {
                setStartDate(e.value[0]);
                validateDates(e.value[0], endDate);
            }
        },
        [
            setStartDate,
            endDate
        ]
    );

    // Обработчик изменения даты конца
    const onChangeEndDate = useCallback(
        (e: CalendarChangeEvent) => {
            if (e.value instanceof Date) {
                setEndDate(e.value);
                validateDates(startDate, e.value);
            } else if (Array.isArray(e.value) && e.value[0] instanceof Date) {
                setEndDate(e.value[0]);
                validateDates(startDate, e.value[0]);
            }
        },
        [
            setEndDate,
            startDate
        ]
    );

    // Обработчик изменения названия задачи
    const onChangeName = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement>) => {
            setTaskName(event.target.value);
        },
        [setTaskName]
    );

    // Подтверждение действия
    const onConfirm = useCallback(() => {
        setOpenConfirmWindow(false);
    }, [setOpenConfirmWindow]);

    // Смена страницы/вкладки
    const changePage = useCallback(
        (id: number) => {
            setTaskDesc(value);
            setTab(id);
        },
        [setTaskDesc, setTab, value]
    );

    // Удаление тегов
    const removeTags = useCallback(
        async (_tags: ITag | ITag[]) => {
            setTags((prevTags) => {
                if (Array.isArray(_tags)) {
                    const tagIdsToRemove = new Set(_tags.map((tag) => tag.id));
                    return prevTags?.filter((tag) => !tagIdsToRemove.has(tag.id)) || [];
                } else {
                    return prevTags?.filter((tag) => tag.id === _tags.id) || [];
                }
            });
        },
        [setTags]
    );

    // Получение приоритета
    const getPriority = useCallback(async (): Promise<ITypeAndPriority | undefined> => {
        try {
            const typePriorityData = await store.taskService.getTypeAndPriority();

            if (typePriorityData.status !== 200) {
                showToast("Ошибка загрузки типов и приоритетов!");
                return undefined;
            }

            return typePriorityData.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                showToast(error.response?.data?.message || "Ошибка сети");
            } else if (error instanceof Error) {
                showToast(error.message);
            } else {
                showToast("Неизвестная ошибка при загрузке приоритетов");
            }
            return undefined;
        }
    }, [store.taskService, showToast]);

    // Загрузка данных
    const fetchData = useCallback(async () => {
        try {
            const response = await getPriority();
            setTypesPriority(response);
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                showToast(error.response?.data?.message || "Ошибка сети");
            } else if (error instanceof Error) {
                showToast(error.message);
            } else {
                showToast("Неизвестная ошибка при загрузке приоритетов");
            }
        }
    }, [setTypesPriority]);

    // Закрытие и очистка дерева
    const closeAndClearTree = useCallback(async () => {
        handleShowLoader(true);

        try {
            closeFunc();
            urlClear(window.location.href, ["page", "entity", "action", "entityId", "checklistId"], navigate);
            store.setDrag(true);
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                showToast(error.response?.data?.message || "Ошибка сети");
            } else if (error instanceof Error) {
                showToast(error.message);
            } else {
                showToast("Ошибка при закрытии дерева");
            }
        } finally {
            handleShowLoader(false);
        }
    }, [closeFunc, setSearchParams, store.setDrag, handleShowLoader]);

    // Загрузка данных из стора
    const loadDataStore = useCallback(() => {
        const save = store.getAddEditTaskData();
        if (!save?.data) return;

        const res: ITaskSaveOptions = save.data;
        const boardIdFromUrl = Number(window.location.pathname.split("/").pop());

        const startDate = res.startDate ? new Date(res.startDate) : undefined;
        const endDate = res.endDate ? new Date(res.endDate) : undefined;
        const requiredTimeInMinutes = res.requiredTime ?? 0;
        const hours = Math.floor(requiredTimeInMinutes / 60);
        const minutes = requiredTimeInMinutes % 60;

        setTaskName(res.name || "");
        setTaskDesc(res.description || "");
        setValue(res.description || "");
        setBoardId(res.boardId ?? boardIdFromUrl);
        setGroup(res.group || []);
        setPriority(res.priorityId);
        setType(res.type);
        setStartDate(startDate);
        setEndDate(endDate);
        setResponsible(res.responsible);
        setPreEditResp(res.responsible);
        setCustomHours(hours);
        setCustomMinutes(minutes);
        setTimeSelectVariant(hours);
        setPercentageCompletion(res.percentageCompletion ?? 0);
    }, [
        store,
        setTaskName,
        setTaskDesc,
        setValue,
        setBoardId,
        setGroup,
        setPriority,
        setType,
        setStartDate,
        setEndDate,
        setResponsible,
        setCustomHours,
        setCustomMinutes,
        setTimeSelectVariant,
        setPercentageCompletion,
    ]);

    // Получение начальных данных
    const fetchInitialData = useCallback(async () => {
        try {
            await fetchData();
        } catch (error) {
            if (error instanceof AxiosError) {
                showToast(error.response?.data?.message || "Ошибка сети");
            } else if (error instanceof Error) {
                showToast(error.message);
            } else {
                showToast("Произошла ошибка при загрузке данных");
            }
        }
    }, [fetchData, showToast]);

    // Загрузка данных борды
    const loadBoardsIfNeeded = useCallback(async () => {
        if (columnId !== undefined) return;

        try {
            const boardsRaw = await store.dashBoardService.getProjectBoards(projectId ?? store.currentProjectId);

            if (boardsRaw?.data) {
                const dataBoards = boardsRaw.data.filter((xx) => xx.isProxyBoard === false);
                if (dataBoards.length > 0) {
                    setBoards(dataBoards);
                    setBoardId(dataBoards[0].id);
                    setBoardName(dataBoards[0].name);
                    return;
                }
            }

            setBoardId(undefined);
            setBoardName("Нет досок для добавления");
        } catch (error) {
            if (error instanceof AxiosError) {
                showToast(error.response?.data?.message || "Ошибка сети");
            } else if (error instanceof Error) {
                showToast(error.message);
            } else {
                showToast("Ошибка загрузки досок");
            }
        }
    }, [columnId, projectId, store.currentProjectId, store.dashBoardService]);

    // Загрузка данных задачи
    const loadTaskData = useCallback(async () => {
        if (taskId === undefined) return;

        try {
            const response = await store.taskService.getTaskForm(taskId);
            const res = response.data;

            if (res.projectId) {
                store.setCurrentProjectId(res.projectId);
            }

            // Проверка на старую дату
            const [massDateDay, massDateMonth, massDateYear] = OLD_DATE.split(".");
            const checkDate = new Date(Number(massDateYear), parseInt(massDateMonth, 10) - 1, Number(massDateDay));
            const createdAt = res.createdAt ? new Date(res.createdAt) : undefined;
            const updatedAt = res.updatedAt ? new Date(res.updatedAt) : undefined;
            let isOldTask = updatedAt && createdAt && createdAt < checkDate && updatedAt < checkDate;
            //не знал какую логику тут поставить, вроде не должно сломаться
            if (isOldTask === undefined) isOldTask = false;

            if (isOldTask) {
                setShowWarning(true);
                setOpenOldMark(true);
            }

            // Установка данных задачи
            updateTaskFormState(res, isOldTask);
        } catch (error) {
            if (error instanceof AxiosError) {
                showToast(error.response?.data?.message || "Ошибка загрузки задачи");
            }
        }
    }, [taskId, store.taskService, showToast, OLD_DATE]);

    // Обновление полей задачи
    const updateTaskFormState = useCallback(
        (res: any, isOldTask: boolean) => {
            const sd = res.startDate ? new Date(res.startDate) : undefined;
            const ed = res.endDate ? new Date(res.endDate) : undefined;
            const requiredTimeInMinutes = res.requiredTime;
            const hours = Math.floor(requiredTimeInMinutes / 60);
            const minutes = requiredTimeInMinutes % 60;

            setTaskName(res.name);
            setValue(res.description || "");
            // setBoardId(Number(window.location.pathname.split("/").pop()));
            setPriority(res.priority.id);
            setType(res.type.id);
            setTags(res.tags);
            setStartDate(sd);
            setEndDate(ed);
            setResponsible(res?.responsible);
            setPreEditResp(res?.responsible);
            setCustomHours(hours);
            setCustomMinutes(minutes);
            setTimeSelectVariant(hours);
            setGroup(res.group);
            setPercentageCompletion(res.percentageCompletion);
            setTaskData(res);
            
            if (!isOldTask) {
                setTaskDesc(res.description);
                setTaskOldMarkDesc("");
            } else {
                setTaskDesc("");
                setTaskOldMarkDesc(res.description);
            }
        },
        [
            setTaskData,
            setTaskName,
            setValue,
            setBoardId,
            setPriority,
            setType,
            setTags,
            setStartDate,
            setEndDate,
            setResponsible,
            setCustomHours,
            setCustomMinutes,
            setTimeSelectVariant,
            setGroup,
            setPercentageCompletion,
            setTaskDesc,
            setTaskOldMarkDesc,
        ]
    );

    // Провекра и загрузка параметров из строки
    const checkAndLoadFromParams = useCallback(async () => {
        if (
            (optionsQueryParams.action === "create" || optionsQueryParams.action === "edit") &&
            (optionsQueryParams.entity === "column-task-form" ||
                optionsQueryParams.entity === "sub-task-form" ||
                optionsQueryParams.entity === "task-form")
        ) {
            loadDataStore();
        }
    }, [optionsQueryParams, loadDataStore]);

    // Обработчик выбора доски
    const boardIdSave = useCallback(
        (id: number, name: string) => {
            setBoardId(id);
            setBoardName(name);
            setBoardsSelect(false);
        },
        [setBoardId, setBoardName, setBoardsSelect]
    );

    // Сохранение описания задачи
    const handleSaveTaskDesk = useCallback(
        (content: string) => {
            setValue(content);
        },
        [setValue]
    );

    // Компонент кастомного алерта
    const blockDomCustomAllert = useCallback(
        () => (
            <CustomAlert
                headerText="Уведомление об ошибке"
                bodyText="Вы не можете создать задачу, так как у проекта отсутствуют доски или колонки у доски"
                confirmText="Ок"
                onConfirm={onConfirm}
            />
        ),
        [onConfirm]
    );

    // Переключение просмотра старой метки
    const openOldView = useCallback(() => {
        setOpenOldMark((prevState) => !prevState);
    }, [setOpenOldMark]);

    // Обработчик клика вне области
    const handleClickOutside = useCallback(
        (event: globalThis.MouseEvent) => {
            const target = event.target as Node;

            if (wrapperRef.current?.contains(target) || buttonRef.current?.contains(target)) {
                return;
            }
            setBoardsSelect(false);
        },
        [wrapperRef, buttonRef, setBoardsSelect]
    );

    // #region Эффекты

    useEffect(() => {
        setIsLoading(true);
        const initializeData = async () => {
            store.setDrag(false);
            urlFiller(
                window.location.href,
                {
                    arguments: [
                        createArgumentIfDefined("action", optionsQueryParams.action),
                        createArgumentIfDefined("checklistId", optionsQueryParams.cheklistId),
                        createArgumentIfDefined("entity", optionsQueryParams.entity),
                        createArgumentIfDefined("entityId", optionsQueryParams.entityId),
                        createArgumentIfDefined("page", optionsQueryParams.page),
                    ].filter(Boolean) as IArgument[],
                    fragment: [],
                },
                false,
                navigate
            );

            await fetchInitialData();

            try {
                await loadBoardsIfNeeded();
                setValue("");
                setTaskDesc("");
                setEditValue("");

                await loadTaskData();
                await checkAndLoadFromParams();
                await loadTeamMembers();
            } catch (error) {
                if (error instanceof AxiosError) {
                    showToast(error.response?.data?.message || "Ошибка сети");
                } else if (error instanceof Error) {
                    showToast(error.message);
                } else {
                    showToast(`Initialization error: ${error}`);
                }
            } finally {
                setIsLoading(false);
            }
        };

        initializeData();
    }, []);

    useEffect(() => {
        if (defaultBoardId !== undefined)
            setBoardId(defaultBoardId)
    }, [defaultBoardId])

    // Эффект для обработки кликов вне области
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    useEffect(() => {
        if (taskData && mode === "edit") {
            setPreEditCompileRequest(compileRequest());
        }
    }, [taskData]);

    // #endregion

    // #endregion

    return {
        maxDescription,
        curDescriptionLength,
        setCurDescriptionLength,
        setMaxDescription,

        forceLinkDescription,
        forceLinkExecutor,
        forceLinkName,
        forceLinkTerm,
        taskName,
        taskDesc,
        taskOldMarkDesc,
        taskData,
        typesPriority,
        persons,
        boards,
        boardId,
        boardName,
        responsible,
        type,
        priority,
        tags,
        percentageCompletion,
        customHours,
        customMinutes,
        startDate,
        endDate,
        group,
        value,
        editValue,
        fileCreate,
        isLoading,
        showResponsibleSelector,
        scrollEnded,
        tab,
        showGroupSelector,
        timeSelectVariant,
        openConfirmWindow,
        openBoardsSelect,
        openOldMark,
        showWarning,
        nameError,
        descError,
        datesError,
        priorityError,
        boardError,
        responsibleError,
        typeError,

        // Default setters (empty functions)
        setTaskName,
        setTaskDesc,
        setTaskOldMarkDesc,
        setTaskData,
        setTypesPriority,
        setShowResponsibleSelector,
        setScrollEnded,
        setPersons,
        setBoards,
        setBoardName,
        setResponsible,
        setType,
        setPriority,
        setTags,
        setPercentageCompletion,
        setTab,
        setCustomHours,
        setCustomMinutes,
        setShowGroupSelector,
        setStartDate,
        setEndDate,
        setGroup,
        setValue,
        setEditValue,
        setFileCreate,
        setIsLoading,
        setTimeSelectVariant,
        setOpenConfirmWindow,
        setBoardsSelect,
        setOpenOldMark,
        setShowWarning,
        setNameError,
        setDescError,
        setDatesError,
        setPriorityError,
        setBoardError,
        setResponsibleError,
        setTypeError,

        // Default function implementations
        calculateRequiredTime,
        filterTags,
        normalizeDescription,
        normalizeDate,
        compileRequest,
        compileSaveParam,
        handleTaskAction,
        renderTaskOptions,
        taskTypeDisplay,
        taskPriorityDisplay,
        handleTypeSelection,
        handlePrioritySelection,
        onChangeCustomTime,
        selectTimeVariant,
        handleGroupSelection,
        onAddTags,
        closeResponsibleSelector,
        handleResponsibleSelection,
        handleGroupRemove,
        appendPersons,
        onChangeStartDate,
        onChangeEndDate,
        openResponsibleSelector,
        clearResponsible,
        onChangeName,
        onConfirm,
        changePage,
        closeGroupSelector,
        loadPersons,
        removeTags,
        openGroupSelector,
        getPriority,
        fetchData,
        closeAndClearTree,
        loadDataStore,
        fetchInitialData,
        loadTaskData,
        updateTaskFormState,
        checkAndLoadFromParams,
        loadTeamMembers,
        boardIdSave,
        handleSaveTaskDesk,
        blockDomCustomAllert,
        openOldView,
        handleClickOutside,

        wrapperRef,
        buttonRef,

        mode,
        optionsQueryParams,
        taskId,
        closeFunc,
        columnId,
        onTaskAction,
        projectId,
        permission,
        responsibleRequired,
        parentTaskId,
    };
};
